import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type { AdminGroup, AdminUser, OrgUnit } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { Field } from "../../components/surfaces";
import { space } from "../../theme/space";

const { Text } = Typography;

type Props = {
  units: OrgUnit[];
  groups: AdminGroup[];
  onChanged: () => void;
};

/**
 * People: who exists, who they report to, and which groups they are in.
 *
 * The three columns that matter are the ones Django admin buried: **manager**,
 * **unit** and **where the record came from**. A derived approval slot ("the
 * requester's manager signs") is only as good as the first of those, so the
 * table shows it as a first-class column and offers a filter for the people
 * who have none — that list is the work an administrator has to finish before
 * such a slot can resolve at all.
 *
 * A person is never deleted, only deactivated (invariant 2): they authored
 * audit rows and signed permits. Deactivated people stay in the list, greyed,
 * because finding one and switching them back on is most of why this screen
 * exists.
 */
const PeoplePanel = ({ units, groups, onChanged }: Props) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState<number | undefined>();
  const [groupFilter, setGroupFilter] = useState<number | undefined>();
  const [noManagerOnly, setNoManagerOnly] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [passwordFor, setPasswordFor] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        await ApiService.apiAccountsAdminUsersList({
          search: search || undefined,
          org_unit: unitFilter,
          group: groupFilter,
          no_manager: noManagerOnly ? "true" : undefined,
        }),
      );
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the people list")));
    }
    setLoading(false);
  }, [search, unitFilter, groupFilter, noManagerOnly, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const peopleOptions = useMemo(
    () =>
      rows
        .filter((r) => r.is_active)
        .map((r) => ({ value: r.id, label: `${r.name} · ${r.email}` })),
    [rows],
  );

  const openEditor = (user: AdminUser | null) => {
    setCreating(user === null);
    setEditing(user);
    form.setFieldsValue(
      user ?? {
        email: "",
        first_name: "",
        last_name: "",
        job_title: "",
        site: "",
        cost_centre: "",
        employee_id: "",
        manager: null,
        org_unit: null,
        group_ids: [],
      },
    );
  };

  const save = async () => {
    const values = await form.validateFields();
    try {
      if (creating) {
        await ApiService.apiAccountsAdminUsersCreate(values);
        message.success(t("Added."));
      } else if (editing) {
        // `email` is deliberately not sent: the server ignores it anyway, and
        // sending a field that is silently dropped is how a screen ends up
        // looking like it saved something it did not.
        const { email, ...rest } = values;
        await ApiService.apiAccountsAdminUsersPartialUpdate(editing.id, rest);
        message.success(t("Saved."));
      }
      setEditing(null);
      setCreating(false);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save")));
    }
  };

  const deactivate = (user: AdminUser) => {
    Modal.confirm({
      title: t("Switch off {{name}}?", { name: user.name }),
      // Said explicitly, because "delete" is what the button would say
      // anywhere else and this is not that.
      content: t(
        "They keep their history — everything they signed, raised and commented on stays exactly as it is. They simply cannot sign in, and stop appearing in approver pickers.",
      ),
      okText: t("Switch off"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ApiService.apiAccountsAdminUsersDeactivate(user.id);
          load();
          onChanged();
        } catch (e) {
          message.error(apiErrorMessage(e, t("Could not switch them off")));
        }
      },
    });
  };

  const activate = async (user: AdminUser) => {
    try {
      await ApiService.apiAccountsAdminUsersActivate(user.id);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not switch them back on")));
    }
  };

  const setPassword = async () => {
    const values = await passwordForm.validateFields();
    try {
      await ApiService.apiAccountsAdminUsersSetPassword(passwordFor!.id, values);
      message.success(t("Password set."));
      setPasswordFor(null);
      passwordForm.resetFields();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not set the password")));
    }
  };

  const columns = [
    {
      title: t("Name"),
      dataIndex: "name",
      render: (name: string, row: AdminUser) => (
        <div style={{ opacity: row.is_active ? 1 : 0.5 }}>
          <div style={{ fontWeight: 500 }}>
            {name}
            {!row.is_active && (
              <Tag style={{ marginInlineStart: space.tight }}>
                {t("Switched off")}
              </Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.email}
          </Text>
        </div>
      ),
    },
    {
      title: t("Job title"),
      dataIndex: "job_title",
      render: (value: string) => value || <Text type="secondary">—</Text>,
    },
    {
      title: t("Unit"),
      dataIndex: "org_unit_name",
      render: (value: string) => value || <Text type="secondary">—</Text>,
    },
    {
      // The column a derived approval slot lives or dies on.
      title: t("Reports to"),
      dataIndex: "manager_name",
      render: (value: string) =>
        value || (
          <Tooltip
            title={t(
              "Nobody. A slot set to 'the requester's manager' cannot be filled for them.",
            )}
          >
            <Tag color="warning">{t("no manager")}</Tag>
          </Tooltip>
        ),
    },
    {
      title: t("Groups"),
      dataIndex: "group_names",
      render: (names: string[]) =>
        names?.length ? (
          <Space size={4} wrap>
            {names.map((n) => (
              <Tag key={n} style={{ marginInlineEnd: 0 }}>
                {n}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t("Record"),
      dataIndex: "directory_source",
      width: 110,
      render: (source: string) =>
        source && source !== "Manual" ? (
          <Tooltip
            title={t(
              "Kept up to date by the directory. Editing it here lasts until the next sync.",
            )}
          >
            <Tag color="blue">{t("Directory")}</Tag>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("Entered by hand")}
          </Text>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 210,
      render: (_: unknown, row: AdminUser) => (
        <Space size={4}>
          <Button size="small" onClick={() => openEditor(row)}>
            {t("Edit")}
          </Button>
          <Tooltip title={t("Set a password")}>
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => setPasswordFor(row)}
            />
          </Tooltip>
          {row.is_active ? (
            <Button size="small" danger onClick={() => deactivate(row)}>
              {t("Switch off")}
            </Button>
          ) : (
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={() => activate(row)}
            >
              {t("Switch on")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: space.snug,
          alignItems: "flex-end",
          flexWrap: "wrap",
          marginBottom: space.base,
        }}
      >
        <Field label={t("Search")}>
          <Input.Search
            allowClear
            style={{ width: 260 }}
            placeholder={t("Name, address or staff number")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
        <Field label={t("Unit")}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            placeholder={t("Any unit")}
            value={unitFilter}
            onChange={setUnitFilter}
            options={units.map((u) => ({ value: u.id, label: u.name }))}
          />
        </Field>
        <Field label={t("Group")}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            placeholder={t("Any group")}
            value={groupFilter}
            onChange={setGroupFilter}
            options={groups.map((g) => ({ value: g.id, label: g.name }))}
          />
        </Field>
        <Button
          type={noManagerOnly ? "primary" : "default"}
          onClick={() => setNoManagerOnly((v) => !v)}
        >
          {t("Missing a manager")}
        </Button>
        <div style={{ flex: 1 }} />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditor(null)}
          >
            {t("Add somebody")}
          </Button>
        </Space>
      </div>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        pagination={{ pageSize: 25, showSizeChanger: false }}
        scroll={{ x: 1000 }}
      />

      <Modal
        open={editing !== null || creating}
        title={creating ? t("Add somebody") : t("Edit person")}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={save}
        okText={t("Save")}
        cancelText={t("Cancel")}
        width={620}
      >
        <Form form={form} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item
            name="email"
            label={t("Email address")}
            rules={[{ required: true, type: "email" }]}
            extra={
              creating
                ? undefined
                : t(
                    "This is the login and what a directory sync matches on, so it cannot be changed here.",
                  )
            }
          >
            <Input disabled={!creating} />
          </Form.Item>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item
              name="first_name"
              label={t("First name")}
              style={{ flex: 1 }}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="last_name"
              label={t("Last name")}
              style={{ flex: 1 }}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item name="job_title" label={t("Job title")} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="employee_id" label={t("Staff number")} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item name="org_unit" label={t("Unit")} style={{ flex: 1 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={units.map((u) => ({ value: u.id, label: u.name }))}
              />
            </Form.Item>
            <Form.Item
              name="manager"
              label={t("Reports to")}
              style={{ flex: 1 }}
              extra={t("Used by approval slots set to 'the requester's manager'.")}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={peopleOptions.filter((o) => o.value !== editing?.id)}
              />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item name="site" label={t("Site")} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="cost_centre" label={t("Cost centre")} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            name="group_ids"
            label={t("Groups")}
            extra={t(
              "Groups decide which templates somebody sees and which approval pools they belong to.",
            )}
          >
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </Form.Item>
          {creating && (
            <Form.Item
              name="password"
              label={t("Password")}
              extra={t(
                "Leave this empty for somebody who will sign in through the company directory.",
              )}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={passwordFor !== null}
        title={t("Set a password for {{name}}", { name: passwordFor?.name ?? "" })}
        onCancel={() => setPasswordFor(null)}
        onOk={setPassword}
        okText={t("Set it")}
        cancelText={t("Cancel")}
      >
        <Form form={passwordForm} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item
            name="new_password"
            label={t("New password")}
            rules={[{ required: true, min: 8 }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PeoplePanel;
